import { memo } from 'react';

import { Wrench } from 'lucide-react';

import { formatMessageTimestamp } from '@/lib/message-time';
import { cn } from '@/lib/utils';

import type { TimelineToolItem } from '@repo/shared-types';

/**
 * System/tool-call event in the conversation feed: a collapsed, centered chip
 * that expands to a monospace dump of the tool arguments. Memoized so parent
 * re-renders don't re-stringify the arguments for every chip.
 */
const ToolCallBubbleComponent = ({
  item,
  dark = false,
}: {
  item: TimelineToolItem;
  dark?: boolean;
}) => {
  const hasArgs = item.arguments && Object.keys(item.arguments).length > 0;

  return (
    <div className="flex justify-center py-0.5">
      <details
        className={cn(
          'max-w-[85%] min-w-0 rounded-lg border px-2.5 py-1 text-xs',
          dark
            ? 'border-[#d1d7db] bg-[#f0f2f5] text-[#54656f] dark:border-[#2a3942] dark:bg-[#182229] dark:text-[#aebac1]'
            : 'bg-muted/60 text-muted-foreground border-dashed',
        )}
      >
        <summary className="flex cursor-pointer items-center gap-1.5 select-none">
          <Wrench className="h-3 w-3 shrink-0 opacity-70" />
          <span className="font-mono font-medium">{item.tool_name}</span>
          <span className="text-[10px] opacity-60">{formatMessageTimestamp(item.timestamp)}</span>
        </summary>
        <pre
          className={cn(
            'mt-1.5 max-h-48 overflow-auto rounded p-1.5 font-mono text-[11px] leading-relaxed whitespace-pre-wrap',
            dark ? 'bg-white dark:bg-[#0b141a]' : 'bg-background/80',
          )}
        >
          {hasArgs ? JSON.stringify(item.arguments, null, 2) : 'No arguments recorded'}
        </pre>
      </details>
    </div>
  );
};

export const ToolCallBubble = memo(ToolCallBubbleComponent);
